import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import Chip from "@/components/ui/chip";
import FileImage from "@/components/ui/file-image";
import ConfirmModal from "@/components/ui/confirm-modal";
import { ArrowRightLong } from "@/components/icons";
import type { FilterField, SortOption, TableColumn } from "@/types/component";
import type {
  ClientEducationRequest,
  ClientEducationRequestStatus,
} from "../types/client-education-requests.types";
import { clientEducationRequestsApi } from "../services/client-education-requests.api";
import styles from "./ClientEducationRequestsPage.module.scss";

// Ara onay statusları (0 REQUESTED, 15 APPROVED_BY_EDUCATION_SUPERVISOR, 1 APPROVED_BRANCH_MANAGER)
// kullanıcıya "Talep gönderildi" olarak gösterilir. Final onay (2 ENROLLED) ve onay sonrası
// içerik/sınav/anket aşamaları "Onaylandı" olarak gösterilir. 9 COMPLETED / 11 NOT_COMPLETED ve
// 6 CONTENT_COMPLETED "Tamamlandı" etiketini alır.
const STATUS_LABEL_KEYS: Record<number, string> = {
  0: "clientEducationRequests.filters.statusRequested",
  15: "clientEducationRequests.filters.statusRequested",
  1: "clientEducationRequests.filters.statusRequested",
  2: "clientEducationRequests.filters.statusEnrolled",
  3: "clientEducationRequests.filters.statusRejectedBranchManager",
  4: "clientEducationRequests.filters.statusRejectedCentralManager",
  25: "clientEducationRequests.filters.statusRejectedCentralManager",
  5: "clientEducationRequests.filters.statusCancelled",
  6: "clientEducationRequests.filters.statusCompleted",
  7: "clientEducationRequests.filters.statusEnrolled",
  8: "clientEducationRequests.filters.statusEnrolled",
  9: "clientEducationRequests.filters.statusCompleted",
  11: "clientEducationRequests.filters.statusCompleted",
  65: "clientEducationRequests.filters.statusEnrolled",
  82: "clientEducationRequests.filters.statusEnrolled",
};

const ClientEducationRequestsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterValues, setFilterValues] = useState<Record<string, unknown>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [requests, setRequests] = useState<ClientEducationRequest[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] =
    useState<ClientEducationRequest | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  const fetchRequests = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const apiStatus =
        filterValues.status != null && filterValues.status !== ""
          ? Number(filterValues.status)
          : undefined;
      const apiType =
        filterValues.format != null && filterValues.format !== ""
          ? Number(filterValues.format)
          : undefined;

      // Tarihi belirlenmiş VE kaydı kesinleşmiş eğitimler "Eğitimlerim"
      // sayfasına düşer, burada tekrar gösterilmez. Onay bekleyen / reddedilen
      // talepler ise eğitimin tarihi olsa da burada kalır: eskiden tarihli
      // eğitime yapılan bekleyen talep iki sayfada da görünmüyordu. Filtre
      // client-side uygulandığı için limit'i yüksek tutup pagination'ı da
      // client-side yapıyoruz.
      const { data } = await clientEducationRequestsApi.getMyEnrollments(
        {
          page: 1,
          limit: 100,
          search: searchQuery.trim() || undefined,
          status: apiStatus,
          type: apiType,
        },
      );
      const filtered = data.filter(
        (row) =>
          !row.hasScheduledDates ||
          row.status === "pending" ||
          row.status === "rejected",
      );
      setRequests(filtered);
      setTotalRecords(filtered.length);
    } catch (err: unknown) {
      const message =
        (
          err as {
            response?: { data?: { message?: string } };
            message?: string;
          }
        )?.response?.data?.message ||
        (err as { message?: string })?.message ||
        t("common.error");
      setError(message);
      setRequests([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    t,
    searchQuery,
    filterValues.status,
    filterValues.format,
  ]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const filterFields: FilterField[] = [
    {
      name: "format",
      label: t("clientEducationRequests.filters.format"),
      type: "dropdown",
      placeholder: t("clientEducationRequests.filters.formatPlaceholder"),
      options: [
        { label: t("requestAndApproval.educationTypes.faceToFace"), value: "0" },
        { label: t("requestAndApproval.educationTypes.online"), value: "1" },
        { label: t("requestAndApproval.educationTypes.unit"), value: "2" },
        { label: t("requestAndApproval.educationTypes.nonUnit"), value: "3" },
      ],
    },
    {
      name: "status",
      label: t("clientEducationRequests.filters.status"),
      type: "dropdown",
      placeholder: t("clientEducationRequests.filters.statusPlaceholder"),
      options: [
        {
          label: t("clientEducationRequests.filters.statusRequested"),
          value: "0",
        },
        {
          label: t("clientEducationRequests.filters.statusApprovedBranchManager"),
          value: "1",
        },
        {
          label: t("clientEducationRequests.filters.statusEnrolled"),
          value: "2",
        },
        {
          label: t("clientEducationRequests.filters.statusRejectedBranchManager"),
          value: "3",
        },
        {
          label: t("clientEducationRequests.filters.statusRejectedCentralManager"),
          value: "4",
        },
        {
          label: t("clientEducationRequests.filters.statusCancelled"),
          value: "5",
        },
        {
          label: t("clientEducationRequests.filters.statusCompleted"),
          value: "6",
        },
      ],
    },
  ];

  const sortOptions: SortOption[] = [
    {
      label: t("clientEducationRequests.sort.newest"),
      value: "newest",
    },
    {
      label: t("clientEducationRequests.sort.oldest"),
      value: "oldest",
    },
  ];

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const handleFilterChange = (filters: Record<string, unknown>) => {
    setFilterValues(filters);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => setCurrentPage(page);
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const goToDetail = (row: ClientEducationRequest) => {
    navigate(`/dashboard/education-catalog/detail?id=${row.educationId}`, {
      state: { from: "client-education-requests" },
    });
  };

  const handleConfirmCancel = async () => {
    if (!cancelTarget) return;
    setIsCancelling(true);
    try {
      await clientEducationRequestsApi.cancelMyRequest(cancelTarget.id);
      toast.success(t("clientEducationRequests.cancel.success"));
      setCancelTarget(null);
      fetchRequests();
    } catch (err: unknown) {
      toast.error(
        (err as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || t("clientEducationRequests.cancel.error"),
      );
    } finally {
      setIsCancelling(false);
    }
  };

  const goToEducationCatalog = () => {
    navigate("/dashboard/education-catalog");
  };

  const getStatusChipType = (
    status: ClientEducationRequestStatus,
  ): "warning" | "error" | "success" => {
    if (status === "approved") return "success";
    if (status === "rejected") return "error";
    return "warning";
  };

  const getStatusLabel = (row: ClientEducationRequest) => {
    const status = row.enrollmentStatus;
    const key =
      status != null && STATUS_LABEL_KEYS[status]
        ? STATUS_LABEL_KEYS[status]
        : "clientEducationRequests.filters.statusRequested";
    return t(key);
  };

  const tableColumns: TableColumn<ClientEducationRequest>[] = [
    {
      header: t("clientEducationRequests.table.educationName"),
      accessor: (row) => (
        <div className={styles.tableEducationCell}>
          <div className={styles.tableThumbWrap}>
            <FileImage
              fileId={row.imageFileId}
              fallbackSrc={row.fallbackImageUrl ?? row.image}
              alt=""
              className={styles.tableThumb}
              objectFit="cover"
            />
          </div>
          <span className={styles.tableTitle}>{row.title}</span>
        </div>
      ),
    },
    {
      header: t("clientEducationRequests.table.format"),
      accessor: (row) => row.format,
    },
    {
      header: t("clientEducationRequests.table.category"),
      accessor: (row) => row.category,
    },
    {
      header: t("clientEducationRequests.table.requestCollectionRange"),
      accessor: (row) => {
        const fmt = (d?: string | null) =>
          d && dayjs(d).isValid() ? dayjs(d).format("DD MMM YYYY") : null;
        const start = fmt(row.requestCollectionStartDate);
        const end = fmt(row.requestCollectionEndDate);
        if (start && end) return `${start} – ${end}`;
        return start ?? end ?? "—";
      },
    },
    {
      header: t("clientEducationRequests.table.requestDate"),
      accessor: (row) => row.requestDate,
    },
    {
      header: t("clientEducationRequests.table.status"),
      accessor: (row) => (
        <Chip type={getStatusChipType(row.status)} variant="soft" size="small">
          {getStatusLabel(row)}
        </Chip>
      ),
    },
    {
      header: "",
      accessor: (row) => (
        <div className={styles.rowActions}>
          {row.status === "pending" && (
            <button
              type="button"
              className={styles.cancelButton}
              onClick={(e) => {
                e.stopPropagation();
                setCancelTarget(row);
              }}
            >
              {t("clientEducationRequests.cancel.button")}
            </button>
          )}
          <button
            type="button"
            className={styles.detailButton}
            onClick={(e) => {
              e.stopPropagation();
              goToDetail(row);
            }}
            aria-label={t("clientEducationRequests.viewDetail")}
          >
            <ArrowRightLong width={20} height={20} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <DashboardLayout>
      <Header title={t("clientEducationRequests.title")} />

      <PageHeader
        searchPlaceholder={t("clientEducationRequests.searchPlaceholder")}
        searchValue={searchTerm}
        onSearch={handleSearchChange}
        showSort
        sortOptions={sortOptions}
        onSort={() => {}}
        filters={filterFields}
        onFilterChange={handleFilterChange}
        onAddNew={goToEducationCatalog}
        addButtonLabel={t("clientEducationRequests.requestEducation")}
      />

      {error && <div className={styles.errorMessage}>{error}</div>}

      {isLoading ? (
        <div className={styles.loadingState}>
          {t("common.loading")}
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <Table
            columns={tableColumns}
            data={requests.slice(
              (currentPage - 1) * pageSize,
              currentPage * pageSize,
            )}
            getRowId={(row) => row.id}
            onRowClick={goToDetail}
            currentPage={currentPage}
            pageSize={pageSize}
            totalRecords={totalRecords}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            hasVisibleActions={false}
            renderActions={() => null}
            contentBasedHeight
            emptyMessage={t("clientEducationRequests.noData")}
          />
        </div>
      )}

      <ConfirmModal
        isOpen={cancelTarget != null}
        onClose={() => !isCancelling && setCancelTarget(null)}
        onConfirm={handleConfirmCancel}
        title={t("clientEducationRequests.cancel.title")}
        message={t("clientEducationRequests.cancel.message", {
          name: cancelTarget?.title ?? "",
        })}
        confirmText={t("clientEducationRequests.cancel.confirm")}
        cancelText={t("clientEducationRequests.cancel.dismiss")}
        loading={isCancelling}
        confirmVariant="danger"
      />
    </DashboardLayout>
  );
};

export default ClientEducationRequestsPage;
